import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const RoleMastery = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  return (
    <motion.div initial="hidden" animate="show" variants={container} className="max-w-7xl mx-auto">
      {/* Enhanced header with animation */}
      <motion.div variants={item} className="mb-8 md:mb-12 text-center">
        <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-brown via-[#8B6B4D] to-apache bg-clip-text text-transparent">
          Social Media Manager
        </h1>
        <motion.p {...fadeInUp} className="text-base md:text-lg lg:text-xl text-brown/70 max-w-3xl mx-auto leading-relaxed px-4">
          The complete operational blueprint for a high-performance Social Media Manager.
        </motion.p>
      </motion.div>

      {/* Hero card with enhanced styling */}
      <motion.div variants={item} whileHover={{ y: -8 }} className="mb-8 md:mb-12 px-4">
        <div className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-10 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-brown/10 to-apache/10"></div>
          <div className="relative flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-6">
            <motion.div 
              animate={{ rotate: [0, 5, -5, 0] }} 
              transition={{ duration: 4, repeat: Infinity }} 
              className="p-3 md:p-4 glass-card rounded-xl md:rounded-2xl flex-shrink-0"
            >
              <SafeIcon name="Award" className="w-8 h-8 md:w-10 md:h-10 text-gold" />
            </motion.div>
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-brown mb-3 md:mb-4">Position Summary</h2>
              <p className="text-brown/70 leading-relaxed text-sm md:text-base lg:text-lg">
                A Social Media Manager is responsible for executing multi-platform brand presence, maintaining community relationships, building content calendars, publishing content, maintaining brand tone, tracking performance, and supporting marketing objectives.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced grid cards - responsive layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12 px-4">
        <motion.div variants={item} whileHover={{ y: -8, scale: 1.02 }} className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-8 group">
          <div className="flex items-center mb-4 md:mb-6">
            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }} className="p-2 md:p-3 bg-gradient-to-br from-gold to-apache/50 rounded-xl md:rounded-2xl">
              <SafeIcon name="Target" className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </motion.div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-brown ml-3 md:ml-4">Core Responsibilities</h3>
          </div>
          <ul className="space-y-3 md:space-y-4">
            {[
              'Develop platform strategies',
              'Produce engaging content',
              'Manage publishing schedules',
              'Community management',
              'Track analytics & reporting'
            ].map((resp, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-center text-brown/80">
                <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }} className="w-2 h-2 bg-gold rounded-full mr-3"></motion.div>
                <span className="text-sm md:text-base">{resp}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div variants={item} whileHover={{ y: -8, scale: 1.02 }} className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-8 group">
          <div className="flex items-center mb-4 md:mb-6">
            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }} className="p-2 md:p-3 bg-gradient-to-br from-apache to-green/50 rounded-xl md:rounded-2xl">
              <SafeIcon name="CheckCircle" className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </motion.div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-brown ml-3 md:ml-4">Key Deliverables</h3>
          </div>
          <ul className="space-y-3 md:space-y-4">
            {[
              'Daily Platform Checks',
              'X/Twitter: 6 posts/mo',
              'IG + FB: 11 posts/mo',
              'TikTok: 8 posts/mo',
              'Monthly Analytics Report'
            ].map((del, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-center text-brown/80">
                <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }} className="w-2 h-2 bg-apache rounded-full mr-3"></motion.div>
                <span className="text-sm md:text-base">{del}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div variants={item} whileHover={{ y: -8, scale: 1.02 }} className="glass-card rounded-2xl md:rounded-3xl p-6 md:p-8 group">
          <div className="flex items-center mb-4 md:mb-6">
            <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }} className="p-2 md:p-3 bg-gradient-to-br from-brown to-brown/50 rounded-xl md:rounded-2xl">
              <SafeIcon name="TrendingUp" className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </motion.div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-brown ml-3 md:ml-4">KPIs & Metrics</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            {[
              'Engagement Rate',
              'Follower Growth',
              'Impressions',
              'Click-Through Rate',
              'Response Time',
              'Conversion'
            ].map((kpi, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} whileHover={{ scale: 1.05, y: -2 }} className="glass-card p-2 md:p-3 rounded-xl text-center cursor-pointer">
                <span className="text-xs md:text-sm font-bold text-brown">{kpi}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Enhanced strategic alignment section */}
      <motion.div variants={item} className="glass-card rounded-2xl md:rounded-3xl overflow-hidden mx-4">
        <div className="p-4 md:p-8 border-b border-white/20 bg-gradient-to-r from-gold/10 to-apache/10">
          <h3 className="text-xl md:text-2xl font-bold text-brown">Strategic Alignment</h3>
        </div>
        <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h4 className="text-base md:text-lg font-bold gradient-gold bg-clip-text text-transparent mb-4 md:mb-6">Daily Operations</h4>
            <div className="space-y-4 md:space-y-6">
              <motion.div whileHover={{ x: 8 }} className="flex items-start group cursor-pointer">
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }} className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 gradient-gold rounded-full flex items-center justify-center text-white font-bold mr-3 md:mr-4">1</motion.div>
                <p className="text-brown/80 pt-2 group-hover:text-brown transition-colors text-sm md:text-base">Monitor trends and adapt content accordingly to maintain relevance.</p>
              </motion.div>
              <motion.div whileHover={{ x: 8 }} className="flex items-start group cursor-pointer">
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }} className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 gradient-gold rounded-full flex items-center justify-center text-white font-bold mr-3 md:mr-4">2</motion.div>
                <p className="text-brown/80 pt-2 group-hover:text-brown transition-colors text-sm md:text-base">Coordinate creative asset approval cycles with internal design teams.</p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h4 className="text-base md:text-lg font-bold gradient-apache bg-clip-text text-transparent mb-4 md:mb-6">Growth Focus</h4>
            <div className="space-y-4 md:space-y-6">
              <motion.div whileHover={{ x: 8 }} className="flex items-start group cursor-pointer">
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }} className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 gradient-apache rounded-full flex items-center justify-center text-white font-bold mr-3 md:mr-4">1</motion.div>
                <p className="text-brown/80 pt-2 group-hover:text-brown transition-colors text-sm md:text-base">Run campaigns, partner with influencers, and support paid media initiatives.</p>
              </motion.div>
              <motion.div whileHover={{ x: 8 }} className="flex items-start group cursor-pointer">
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 1.5 }} className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 gradient-apache rounded-full flex items-center justify-center text-white font-bold mr-3 md:mr-4">2</motion.div>
                <p className="text-brown/80 pt-2 group-hover:text-brown transition-colors text-sm md:text-base">Maintain brand voice consistency while experimenting with new formats.</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RoleMastery;